import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Lock, Unlock, Printer, TrendingUp, Receipt, Check } from 'lucide-react';
import { Money } from '@/shared/display/Money';
import { DateDisplay } from '@/shared/display/DateDisplay';
import { PageHeader, StatCard } from '@/cajita/ui';
import { CxDialog } from '@/cajita/overlay';
import { Donut, ProgressBar } from '@/cajita/charts';
import {
  closeSession,
  getSessionSummary,
  listSessions,
  openSession,
  type CashSession,
} from './cash-sessions.api';
import { useCurrentSession } from './use-current-session';

const METHOD_LABELS: Record<string, string> = {
  CASH: 'Efectivo',
  CARD: 'Tarjeta',
  YAPE: 'Yape',
  PLIN: 'Plin',
  TRANSFER: 'Transferencia',
  CHECK: 'Cheque',
  CREDIT_BALANCE: 'Saldo a favor',
};
const DONUT_COLORS = ['#6FD3A1', '#A892E8', '#8FB6EE', '#84CFEE', '#6FC6BC', '#F4C56E', '#F1979A'];
const fmt = (n: number | string, dec = 2): string =>
  'S/ ' + Number(n).toLocaleString('es-PE', { minimumFractionDigits: dec, maximumFractionDigits: dec });

export function CashSessionsPage(): JSX.Element {
  const qc = useQueryClient();
  const current = useCurrentSession();
  const sessions = useQuery({ queryKey: ['cash-sessions'], queryFn: listSessions });

  const [openingFloat, setOpeningFloat] = useState('100');
  const [openNotes, setOpenNotes] = useState('');

  const open = useMutation({
    mutationFn: () => openSession({ openingFloat: Number(openingFloat) || 0, notes: openNotes }),
    onSuccess: () => {
      toast.success('Caja abierta');
      void qc.invalidateQueries({ queryKey: ['cash-session'] });
      void qc.invalidateQueries({ queryKey: ['cash-sessions'] });
      setOpenNotes('');
    },
    onError: (err) =>
      toast.error((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? (err as Error).message),
  });

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader title="Caja" sub="Apertura, cierre y arqueo de caja por turno." />

        {!current.data && (
          <div className="card card-pad" style={{ maxWidth: 440, marginBottom: 24 }}>
            <div className="empty-ico" style={{ width: 44, height: 44, borderRadius: 12, marginBottom: 12 }}>
              <Unlock size={20} />
            </div>
            <div className="t-h2 fw7" style={{ marginBottom: 4 }}>
              Abrir caja
            </div>
            <p className="muted t-sm" style={{ marginBottom: 16 }}>
              Declara cuánto efectivo tienes al inicio del turno. Es el punto de partida del arqueo de cierre.
            </p>
            <div className="col gap12">
              <div className="field">
                <label className="label">Efectivo inicial (S/)</label>
                <input className="input tnum" type="number" inputMode="decimal" min={0} step="0.01" value={openingFloat} onChange={(e) => setOpeningFloat(e.target.value)} />
              </div>
              <div className="field">
                <label className="label">Notas (opcional)</label>
                <input className="input" value={openNotes} onChange={(e) => setOpenNotes(e.target.value)} placeholder="ej. Turno tarde - Caja 1" />
              </div>
              <button className="btn btn-primary btn-block" onClick={() => open.mutate()} disabled={open.isPending}>
                {open.isPending ? 'Abriendo…' : 'Abrir caja'}
              </button>
            </div>
          </div>
        )}

        {current.data && <ActiveDashboard session={current.data} />}

        <div className="card" style={{ marginTop: 24 }}>
          <div className="card-head">
            <div className="card-title">Sesiones recientes</div>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Periodo</th>
                  <th className="right">Inicial</th>
                  <th className="right">Cierre</th>
                  <th className="right">Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {(sessions.data ?? []).map((s) => (
                  <SessionRow key={s.id} session={s} />
                ))}
              </tbody>
            </table>
            {(sessions.data ?? []).length === 0 && <div className="empty"><div className="muted">Sin sesiones aún.</div></div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function ActiveDashboard({ session }: { session: CashSession }): JSX.Element {
  const qc = useQueryClient();
  const [arqueo, setArqueo] = useState(false);
  const [declared, setDeclared] = useState('');
  const [closeNotes, setCloseNotes] = useState('');

  const summary = useQuery({
    queryKey: ['cash-session', session.id, 'summary'],
    queryFn: () => getSessionSummary(session.id),
    refetchInterval: 10_000,
  });

  const close = useMutation({
    mutationFn: () => closeSession(session.id, { declaredCash: Number(declared) || 0, notes: closeNotes }),
    onSuccess: () => {
      toast.success('Caja cerrada');
      void qc.invalidateQueries({ queryKey: ['cash-session'] });
      void qc.invalidateQueries({ queryKey: ['cash-sessions'] });
      setArqueo(false);
      setDeclared('');
      setCloseNotes('');
    },
    onError: (err) =>
      toast.error((err as { response?: { data?: { message?: string } } }).response?.data?.message ?? (err as Error).message),
  });

  const methods = summary.data?.paymentsByMethod ?? [];
  const expected = summary.data?.expectedCash ?? '0';
  const salesTotal = Number(summary.data?.salesTotal ?? 0);
  const salesCount = summary.data?.salesCount ?? 0;
  const diffNum = declared ? Number(declared) - Number(expected) : null;
  const totalCobros = methods.reduce((s, m) => s + Number(m.amount), 0);
  const efectivoRecibido = Number(methods.find((m) => m.method === 'CASH')?.amount ?? 0);

  return (
    <>
      <div className="row between aie wrap" style={{ gap: 12, marginBottom: 16, marginTop: -6 }}>
        <div />
        <div className="row gap10 wrap">
          <button className="btn btn-secondary">
            <Printer size={17} />
            Reporte X
          </button>
          <button className="btn btn-dark" onClick={() => setArqueo(true)} disabled={summary.isLoading}>
            <Lock size={17} />
            Cerrar caja
          </button>
        </div>
      </div>

      <div className="grid grid-collapse" style={{ gridTemplateColumns: '1.1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card card-pad" style={{ background: 'var(--ink)', borderColor: 'transparent', color: '#fff' }}>
          <div className="row between aic">
            <span className="t-sm fw6" style={{ color: '#94A3B8' }}>
              Efectivo esperado en caja
            </span>
          </div>
          <div className="t-display tnum" style={{ color: '#fff', marginTop: 14 }}>
            {fmt(expected)}
          </div>
          <div className="row gap16" style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,.1)' }}>
            <div>
              <div className="t-xs" style={{ color: '#94A3B8' }}>
                Inicial
              </div>
              <div className="tnum fw6" style={{ color: '#E5E7EB' }}>
                {fmt(session.openingFloat)}
              </div>
            </div>
            <div>
              <div className="t-xs" style={{ color: '#94A3B8' }}>
                + Ventas efectivo
              </div>
              <div className="tnum fw6" style={{ color: '#84CC16' }}>
                {fmt(efectivoRecibido)}
              </div>
            </div>
          </div>
        </div>
        <StatCard label="Ventas del turno" value={fmt(salesTotal)} icon={<TrendingUp size={19} />} accent="#22C55E" sub={`${salesCount} comprobante(s)`} />
        <StatCard
          label="Ticket promedio"
          value={fmt(salesCount > 0 ? salesTotal / salesCount : 0)}
          icon={<Receipt size={19} />}
          accent="#3B82F6"
          sub="por venta"
        />
      </div>

      <div className="grid grid-collapse" style={{ gridTemplateColumns: '1fr 1.4fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-head">
            <div className="card-title">Cobros por método</div>
          </div>
          <div className="card-pad col aic">
            {methods.length > 0 ? (
              <Donut
                size={184}
                thickness={26}
                data={methods.map((m, i) => ({
                  label: METHOD_LABELS[m.method] ?? m.method,
                  value: Number(m.amount),
                  color: DONUT_COLORS[i % DONUT_COLORS.length]!,
                }))}
                centerLabel={fmt(totalCobros, 0)}
                centerSub="cobrado hoy"
                fmtCenter={(v) => fmt(v, 0)}
              />
            ) : (
              <div className="empty"><div className="muted">Sin cobros aún.</div></div>
            )}
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <div className="card-title">Detalle por método</div>
            <span className="t-xs muted">{methods.reduce((s, m) => s + m.count, 0)} cobros</span>
          </div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Método</th>
                  <th className="right">N° cobros</th>
                  <th className="right">Monto</th>
                  <th className="right">%</th>
                </tr>
              </thead>
              <tbody>
                {methods.map((m, i) => {
                  const pct = totalCobros > 0 ? (Number(m.amount) / totalCobros) * 100 : 0;
                  const color = DONUT_COLORS[i % DONUT_COLORS.length]!;
                  return (
                    <tr key={m.method}>
                      <td>
                        <div className="row aic gap10">
                          <span style={{ width: 10, height: 10, borderRadius: 3, background: color, flex: 'none' }} />
                          <span className="strong">{METHOD_LABELS[m.method] ?? m.method}</span>
                        </div>
                      </td>
                      <td className="right tnum">{m.count}</td>
                      <td className="right tnum strong">{fmt(m.amount)}</td>
                      <td className="right">
                        <span className="row aic gap8" style={{ justifyContent: 'flex-end' }}>
                          <span style={{ width: 44 }}>
                            <ProgressBar value={pct} color={color} height={6} />
                          </span>
                          <span className="tnum muted" style={{ width: 38 }}>
                            {pct.toFixed(0)}%
                          </span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {methods.length === 0 && (
                  <tr>
                    <td colSpan={4} className="muted">
                      Sin cobros aún.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {arqueo && (
        <CxDialog
          title="Arqueo de caja"
          sub="Cuenta el efectivo físico para cerrar el turno"
          icon={<Lock size={20} />}
          iconTone="warning"
          width={440}
          onClose={() => setArqueo(false)}
          footer={
            <>
              <button className="btn btn-secondary grow" onClick={() => setArqueo(false)}>
                Cancelar
              </button>
              <button className="btn btn-dark grow" disabled={!declared || close.isPending} onClick={() => close.mutate()}>
                <Check size={18} />
                {close.isPending ? 'Cerrando…' : 'Cerrar turno'}
              </button>
            </>
          }
        >
          <div className="col gap14">
            <div className="row between aic" style={{ padding: '12px 14px', background: 'var(--canvas)', borderRadius: 11 }}>
              <span className="muted fw6 t-sm">Efectivo esperado</span>
              <span className="tnum fw7">{fmt(expected)}</span>
            </div>
            <div className="field">
              <label className="label">Efectivo contado (físico)</label>
              <input className="input tnum" placeholder="0.00" value={declared} onChange={(e) => setDeclared(e.target.value)} autoFocus />
            </div>
            <div className="field">
              <label className="label">Notas de cierre (opcional)</label>
              <input className="input" value={closeNotes} onChange={(e) => setCloseNotes(e.target.value)} />
            </div>
            {diffNum !== null && (
              <div
                className="row between aic"
                style={{
                  padding: '12px 14px',
                  borderRadius: 11,
                  background: diffNum === 0 ? 'var(--success-soft)' : diffNum < 0 ? 'var(--error-soft)' : 'var(--warning-soft)',
                }}
              >
                <span className="fw6 t-sm">{diffNum === 0 ? 'Caja cuadrada ✓' : diffNum < 0 ? 'Faltante' : 'Sobrante'}</span>
                <span className="tnum fw8">{fmt(diffNum)}</span>
              </div>
            )}
          </div>
        </CxDialog>
      )}
    </>
  );
}

function SessionRow({ session }: { session: CashSession }): JSX.Element {
  const isOpen = session.status === 'OPEN';
  return (
    <tr>
      <td>
        <div className="row aic gap10">
          <span
            className="av"
            style={{ width: 30, height: 30, background: isOpen ? 'var(--success-soft)' : 'var(--canvas)', color: isOpen ? '#15803D' : 'var(--muted)' }}
          >
            {isOpen ? <Unlock size={14} /> : <Lock size={14} />}
          </span>
          <span className="strong">
            <DateDisplay value={session.openedAt} />
            {session.closedAt ? <> → <DateDisplay value={session.closedAt} /></> : <span className="pos"> · en curso</span>}
          </span>
        </div>
      </td>
      <td className="right tnum">
        <Money value={session.openingFloat} />
      </td>
      <td className="right tnum">{session.declaredCash !== null ? <Money value={session.declaredCash} /> : <span className="muted">—</span>}</td>
      <td className="right">
        {session.cashDifference !== null && Number(session.cashDifference) !== 0 ? (
          <span className={`badge badge-${Number(session.cashDifference) > 0 ? 'amber' : 'red'}`}>
            <Money value={session.cashDifference} />
          </span>
        ) : (
          <span className="badge badge-green">Cuadrado</span>
        )}
      </td>
    </tr>
  );
}
